import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Plus, User } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

export default function PartyCombobox({ value, onChange, testid = "field-party_name" }) {
  const [open, setOpen] = useState(false);
  const [parties, setParties] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/parties").then((r) => setParties(r.data || [])).catch(() => setParties([]));
  }, []);

  const q = query.trim();
  const exists = parties.some((p) => p.name.toLowerCase() === q.toLowerCase());

  const pick = (name) => { onChange(name); setOpen(false); setQuery(""); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between font-normal" data-testid={testid}>
          <span className={value ? "" : "text-muted-foreground"}>
            {value || "Select or add party…"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput placeholder="Search party…" value={query} onValueChange={setQuery} data-testid="party-search" />
          <CommandList>
            {q && !exists && (
              <CommandGroup>
                <CommandItem value={`__add__${q}`} onSelect={() => pick(q)} data-testid="party-add-new">
                  <Plus className="mr-2 h-4 w-4 text-primary" />
                  Add "<span className="font-medium">{q}</span>" as new party
                </CommandItem>
              </CommandGroup>
            )}
            <CommandEmpty>No parties found.</CommandEmpty>
            <CommandGroup heading="Existing parties">
              {parties.map((p) => (
                <CommandItem key={p.name} value={p.name} onSelect={() => pick(p.name)} data-testid={`party-option-${p.name}`}>
                  <User className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">{p.type}</span>
                  <Check className={`ml-2 h-4 w-4 ${value === p.name ? "opacity-100" : "opacity-0"}`} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
